// src/hooks/useShopifyImportJob.ts

import { useCallback, useEffect, useRef, useState } from "react";
import {
	getShopifyImportStatus,
} from "../api/inventory/shopifyApi";
import type {
	ShopifyImportQueuedResponse,
	ShopifyImportResults,
	ShopifyImportStatusResponse,
} from "../types/inventory/ShopifyTypes";


/** Imports finish in seconds-to-minutes; 3s stays responsive without hammering. */
export const SHOPIFY_IMPORT_POLL_INTERVAL_MS = 3_000;

/** One tracking window. Elapsing pauses tracking; it never fails the job. */
export const SHOPIFY_IMPORT_TRACKING_WINDOW_MS = 30 * 60_000;

/** Tolerate a transient blip, then pause rather than guess at the job's fate. */
export const MAX_CONSECUTIVE_POLL_ERRORS = 3;

/** Shown whenever tracking pauses; never claims the import itself failed. */
export const TRACKING_PAUSED_MESSAGE = "Unable to check import status right now. The import may still be running.";

export type ShopifyImportPhase = "idle" | "queued" | "processing" | "completed" | "failed";

export interface UseShopifyImportJobOptions {
	/** Operator-facing noun, e.g. "Customer" / "Order". */
	label: string;
	/** The POST that queues the import and returns the 202 body. */
	start: () => Promise<ShopifyImportQueuedResponse>;
	onCompleted?: (results: ShopifyImportResults | null) => void;
	onFailed?: (results: ShopifyImportResults | null, message: string, detail?: string) => void;
	intervalMs?: number;
	trackingWindowMs?: number;
}

export interface UseShopifyImportJobResult {
	phase: ShopifyImportPhase;
	/** Status tracking is actively running. */
	running: boolean;
	/** An unresolved backend job exists — no second import may be started. */
	busy: boolean;
	/** Tracking stopped without a terminal answer; the job id is retained. */
	trackingPaused: boolean;
	/** The retained backend job id while unresolved, else null. */
	activeJobId: string | null;
	results: ShopifyImportResults | null;
	message: string | null;
	run: () => void;
	/** Resume tracking the SAME retained job in a fresh tracking window. */
	resume: () => void;
}

export const useShopifyImportJob = ({
	label,
	start,
	onCompleted,
	onFailed,
	intervalMs = SHOPIFY_IMPORT_POLL_INTERVAL_MS,
	trackingWindowMs = SHOPIFY_IMPORT_TRACKING_WINDOW_MS,
}: UseShopifyImportJobOptions): UseShopifyImportJobResult => {
	const [phase, setPhase] = useState<ShopifyImportPhase>("idle");
	const [results, setResults] = useState<ShopifyImportResults | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [trackingPaused, setTrackingPaused] = useState(false);
	const [activeJobId, setActiveJobId] = useState<string | null>(null);

	const runIdRef = useRef(0);
	const jobIdRef = useRef<string | null>(null);
	const startingRef = useRef(false);
	const trackingRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const mountedRef = useRef(true);

	const startRef = useRef(start);
	const onCompletedRef = useRef(onCompleted);
	const onFailedRef = useRef(onFailed);
	useEffect(() => {
		startRef.current = start;
		onCompletedRef.current = onCompleted;
		onFailedRef.current = onFailed;
	});

	const cancelInFlight = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		abortRef.current?.abort();
		abortRef.current = null;
	}, []);

	useEffect(
		() => () => {
			mountedRef.current = false;
			// Supersede the in-flight window so nothing sets state after unmount.
			runIdRef.current += 1;
			trackingRef.current = false;
			cancelInFlight();
		},
		[cancelInFlight]
	);

	/** Open a fresh tracking window over an already-known job id. */
	const track = useCallback(
		(jobId: string) => {
			const runId = ++runIdRef.current;
			trackingRef.current = true;
			cancelInFlight();

			const alive = () => mountedRef.current && runIdRef.current === runId;

			const resolveJob = (
				nextPhase: "completed" | "failed",
				terminal: ShopifyImportResults | null,
				reason: string | null,
				detail?: string
			) => {
				trackingRef.current = false;
				jobIdRef.current = null;
				cancelInFlight();
				if (!alive()) return;

				setPhase(nextPhase);
				setResults(terminal);
				setMessage(reason);
				setTrackingPaused(false);
				setActiveJobId(null);

				if (nextPhase === "completed") {
					onCompletedRef.current?.(terminal);
				} else {
					onFailedRef.current?.(terminal, reason ?? `${label} import failed.`, detail);
				}
			};

			// Tracking stopped without an answer. The job id is KEPT, the phase is
			// kept, and onFailed is deliberately not called.
			const pauseTracking = () => {
				trackingRef.current = false;
				cancelInFlight();
				if (!alive()) return;

				setTrackingPaused(true);
				setMessage(TRACKING_PAUSED_MESSAGE);
			};

			const startedAt = Date.now();
			let consecutiveErrors = 0;

			function schedule() {
				if (!alive()) return;
				timerRef.current = setTimeout(() => {
					void poll();
				}, intervalMs);
			}

			async function poll() {
				if (!alive()) return;

				let status: ShopifyImportStatusResponse;
				const controller = new AbortController();
				abortRef.current = controller;

				try {
					status = await getShopifyImportStatus(jobId, { signal: controller.signal });
					consecutiveErrors = 0;
				} catch {
					// A timeout, abort or network error is a TRACKING error only.
					if (!alive()) return;

					consecutiveErrors += 1;
					if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
						pauseTracking();
						return;
					}
					schedule();
					return;
				}

				if (!alive()) return;

				const terminal = status?.results ?? null;

				if (status?.status === "completed") {
					resolveJob("completed", terminal, null);
					return;
				}

				if (status?.status === "failed") {
					resolveJob(
						"failed",
						terminal,
						`${label} import failed.`,
						typeof status.error === "string" && status.error !== "" ? status.error : undefined
					);
					return;
				}

				if (status?.status === "queued" || status?.status === "processing") {
					setPhase(status.status);

					if (Date.now() - startedAt >= trackingWindowMs) {
						pauseTracking();
						return;
					}

					schedule();
					return;
				}

				// An unrecognised status is a real contract break, not a blip.
				resolveJob("failed", null, `${label} import returned an unrecognised status.`);
			}

			void poll();
		},
		[cancelInFlight, intervalMs, label, trackingWindowMs]
	);

	const run = useCallback(() => {
		// A retained job id means a backend import is still unresolved.
		if (startingRef.current || trackingRef.current || jobIdRef.current !== null) {
			return;
		}
		startingRef.current = true;

		const runId = ++runIdRef.current;
		cancelInFlight();

		const alive = () => mountedRef.current && runIdRef.current === runId;

		const failToStart = (reason: string) => {
			startingRef.current = false;
			jobIdRef.current = null;
			if (!alive()) return;
			setPhase("failed");
			setResults(null);
			setMessage(reason);
			setActiveJobId(null);
			onFailedRef.current?.(null, reason);
		};

		const begin = async () => {
			setPhase("queued");
			setResults(null);
			setMessage(null);
			setTrackingPaused(false);
			setActiveJobId(null);

			let queued: ShopifyImportQueuedResponse;
			try {
				queued = await startRef.current();
			} catch {
				failToStart(`${label} import could not be started.`);
				return;
			}

			startingRef.current = false;

			if (!alive()) return;

			// CU-86d42ap9h — with no job id there is nothing to track; stop here
			// rather than loop against an undefined status URL.
			if (typeof queued?.job_id !== "string" || queued.job_id === "") {
				failToStart(`${label} import did not return a job to track.`);
				return;
			}

			jobIdRef.current = queued.job_id;
			setActiveJobId(queued.job_id);
			track(queued.job_id);
		};

		void begin();
	}, [cancelInFlight, label, track]);

	const resume = useCallback(() => {
		const jobId = jobIdRef.current;
		if (jobId === null || trackingRef.current || startingRef.current) {
			return;
		}

		setTrackingPaused(false);
		setMessage(null);
		// A fresh window, so resuming does not immediately pause again.
		track(jobId);
	}, [track]);

	const busy = activeJobId !== null || phase === "queued";

	return {
		phase,
		running: busy && !trackingPaused,
		busy,
		trackingPaused,
		activeJobId,
		results,
		message,
		run,
		resume,
	};
};

export default useShopifyImportJob;
